import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X, ChevronDown, Shield, BarChart3, Megaphone, Settings2, Check } from 'lucide-react';

interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
}

const COOKIE_CONSENT_KEY = 'cookie-consent';
export const COOKIE_PREFERENCES_EVENT = 'open-cookie-preferences';

const defaultPreferences: CookiePreferences = {
  essential: true,
  analytics: false,
  marketing: false,
  preferences: false,
};

const cookieCategories = [
  {
    id: 'essential' as const,
    name: 'Essential',
    description: 'Required for the website to function. Cannot be disabled.',
    icon: Shield,
    required: true,
  },
  {
    id: 'analytics' as const,
    name: 'Analytics',
    description: 'Help us understand how visitors interact with our website.',
    icon: BarChart3,
    required: false,
  },
  {
    id: 'marketing' as const,
    name: 'Marketing',
    description: 'Used to deliver personalized advertisements.',
    icon: Megaphone,
    required: false,
  },
  {
    id: 'preferences' as const,
    name: 'Preferences',
    description: 'Remember your settings and personalization choices.',
    icon: Settings2,
    required: false,
  },
];

export const openCookiePreferences = () => {
  window.dispatchEvent(new CustomEvent(COOKIE_PREFERENCES_EVENT));
};

const CookieConsent: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState<CookiePreferences>(defaultPreferences);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.preferences) {
          setPreferences(parsed.preferences);
        }
      } catch {
        // ignore parse errors
      }
    } else {
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  useEffect(() => {
    const handleOpen = () => {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          if (parsed.preferences) {
            setPreferences(parsed.preferences);
          }
        } catch {
          // ignore parse errors
        }
      }
      setShowDetails(true);
      setIsVisible(true);
    };

    window.addEventListener(COOKIE_PREFERENCES_EVENT, handleOpen);
    return () => window.removeEventListener(COOKIE_PREFERENCES_EVENT, handleOpen);
  }, []);

  const savePreferences = (prefs: CookiePreferences) => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      preferences: prefs,
      timestamp: Date.now(),
    }));
    setIsVisible(false);
  };

  const handleAcceptAll = () => {
    const allAccepted: CookiePreferences = {
      essential: true,
      analytics: true,
      marketing: true,
      preferences: true,
    };
    setPreferences(allAccepted);
    savePreferences(allAccepted);
  };

  const handleRejectAll = () => {
    const onlyEssential: CookiePreferences = {
      essential: true,
      analytics: false,
      marketing: false,
      preferences: false,
    };
    setPreferences(onlyEssential);
    savePreferences(onlyEssential);
  };

  const handleSavePreferences = () => {
    savePreferences(preferences);
  };

  const togglePreference = (key: keyof CookiePreferences) => {
    if (key === 'essential') return;
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 200 }}
          className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
        >
          <div className="max-w-4xl mx-auto">
            <div className="bg-surface border border-border rounded-2xl shadow-soft-xl overflow-hidden backdrop-blur-xl">
              <div className="p-5 md:p-6">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex w-12 h-12 rounded-xl bg-accent/10 items-center justify-center flex-shrink-0">
                    <Cookie className="w-6 h-6 text-accent" />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h3 className="text-lg font-semibold text-text-primary">Cookie Preferences</h3>
                        <p className="mt-1 text-sm text-text-secondary leading-relaxed">
                          We use cookies to enhance your browsing experience, analyze site traffic, and personalize content.
                        </p>
                      </div>
                      <button
                        onClick={() => setIsVisible(false)}
                        className="p-1.5 text-text-tertiary hover:text-text-primary hover:bg-surface-elevated rounded-lg transition-colors flex-shrink-0"
                      >
                        <X size={18} />
                      </button>
                    </div>

                    <AnimatePresence>
                      {showDetails && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="mt-5 space-y-3">
                            {cookieCategories.map((category) => {
                              const Icon = category.icon;
                              const isEnabled = preferences[category.id];

                              return (
                                <div
                                  key={category.id}
                                  className="flex items-center gap-4 p-3 rounded-xl bg-background border border-border/50 hover:border-border transition-colors"
                                >
                                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                                    isEnabled ? 'bg-accent/15 text-accent' : 'bg-surface-elevated text-text-tertiary'
                                  }`}>
                                    <Icon size={18} />
                                  </div>

                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-sm font-medium text-text-primary">{category.name}</span>
                                      {category.required && (
                                        <span className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded bg-accent/10 text-accent">
                                          Required
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-text-tertiary mt-0.5 line-clamp-1">{category.description}</p>
                                  </div>

                                  <button
                                    onClick={() => togglePreference(category.id)}
                                    disabled={category.required}
                                    className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
                                      category.required
                                        ? 'bg-accent/50 cursor-not-allowed'
                                        : isEnabled
                                          ? 'bg-accent'
                                          : 'bg-border'
                                    }`}
                                  >
                                    <motion.div
                                      initial={false}
                                      animate={{ x: isEnabled ? 20 : 2 }}
                                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                                      className={`absolute top-1 w-4 h-4 rounded-full ${
                                        isEnabled ? 'bg-white' : 'bg-text-tertiary'
                                      }`}
                                    />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>

                    <div className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                      <button
                        onClick={() => setShowDetails(!showDetails)}
                        className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-medium text-text-secondary hover:text-text-primary border border-border hover:border-border-light rounded-xl transition-colors"
                      >
                        <span>{showDetails ? 'Hide' : 'Customize'}</span>
                        <ChevronDown
                          size={14}
                          className={`transition-transform duration-200 ${showDetails ? 'rotate-180' : ''}`}
                        />
                      </button>

                      <div className="flex-1" />

                      <div className="flex gap-3">
                        <button
                          onClick={handleRejectAll}
                          className="flex-1 sm:flex-none px-4 py-2.5 text-sm font-medium text-text-secondary hover:text-text-primary border border-border hover:border-border-light rounded-xl transition-colors"
                        >
                          Reject All
                        </button>

                        {showDetails ? (
                          <button
                            onClick={handleSavePreferences}
                            className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-medium text-background bg-accent hover:bg-teal-bright rounded-xl transition-colors"
                          >
                            <Check size={16} />
                            Save Preferences
                          </button>
                        ) : (
                          <button
                            onClick={handleAcceptAll}
                            className="flex-1 sm:flex-none px-5 py-2.5 text-sm font-medium text-background bg-accent hover:bg-teal-bright rounded-xl transition-colors"
                          >
                            Accept All
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="h-1 bg-gradient-to-r from-accent via-teal-bright to-accent" />
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CookieConsent;
